class ProductConfigurator extends HTMLElement {
  constructor() {
    super();
    this.type = this.dataset.type;
    this.options = {};
    this.isUpdating = false;
    this.debouncedOnConfiguratorChange = debounce((event) => {
      this.onConfiguratorChange(event);
    }, 300);
  }

connectedCallback() {
  this.init();
}

init() {
  this.details = this.querySelectorAll('details');
  this.inputList = this.querySelectorAll('input[type="radio"]');
  
  // Get product data first
  this.getProductData();
  
  // Handle URL parameters first if they exist
  if (this.type == 'cart') {
    const url = this.closest('edit-dialog').dataset.url;
    const queryString = url.split('?')[1]; // Get everything after the '?'
    const urlParams = new URLSearchParams(queryString);
    this.setProductFromUrl(urlParams);
  } else {
    // Handle regular URL parameters for non-cart pages
    const urlParams = new URLSearchParams(window.location.search);
    const productID = urlParams.get('product');
    
    if (productID) {
      this.setProductFromUrl(urlParams);
    } else {
      // Try raw option params first (e.g. ?materiaal=Eiken&kleur=Wit)
      const appliedFromUrl = this.setOptionsFromUrl(urlParams);

      if (!appliedFromUrl) {
        const firstFieldset = this.querySelector('fieldset.js-option-fieldset');
        if (firstFieldset) {
          const firstInput = firstFieldset.querySelector('input[type="radio"]');
          if (firstInput && !firstInput.checked) {
            firstInput.checked = true;
          }
        }

        this.updateState(this.querySelector('input[type="radio"]:checked'));
      }
    }
  }

  this.addEventListener('change', this.debouncedOnConfiguratorChange.bind(this));
}

updateState(target) {
  this.classList.add('loading');
  
  this.updateOptions(target);

  this.updateAvailableOptions(target);

  this.writeOptionsToUrl();

  this.updateMasterId();
}

getCurrentOptionParams() {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(this.options || {})) {
    if (value) params.set(key, value);
  }
  return params;
}

writeOptionsToUrl() {
  if (this.type === 'cart') return;

  const urlParams = new URLSearchParams(window.location.search);

  // Clear any existing params that match a fieldset on this configurator,
  // so removed/changed options don't linger in the URL.
  this.querySelectorAll('fieldset.js-option-fieldset').forEach((fieldset) => {
    const key = fieldset.dataset.optionType;
    if (key) urlParams.delete(key);
  });

  for (const [key, value] of Object.entries(this.options || {})) {
    if (value) urlParams.set(key, value);
  }

  const newQuery = urlParams.toString();
  const newUrl = `${window.location.pathname}${newQuery ? '?' + newQuery : ''}${window.location.hash}`;
  window.history.replaceState({}, '', newUrl);
}

// New method to ensure all fieldsets have an option selected
ensureAllFieldsetsHaveSelection() {
  const allFieldsets = Array.from(this.querySelectorAll('fieldset.js-option-fieldset'));
  
  allFieldsets.forEach((fieldset, index) => {
    // Skip the first fieldset as it's already handled in init
    if (index === 0) return;
    
    const optionType = fieldset.dataset.optionType;
    
    const selectedInput = fieldset.querySelector('input[type="radio"]:checked');
    if (!selectedInput) {
      // Find the first available input
      const availableInputs = Array.from(fieldset.querySelectorAll('input[type="radio"]:not([disabled])'));
      
      if (availableInputs.length > 0) {
        const firstAvailableInput = availableInputs[0];
        firstAvailableInput.checked = true;
        
        // Manually update the options object
        this.options[optionType] = firstAvailableInput.value;
        this.updateTitles(firstAvailableInput);
      }
    } else {
      // Make sure the option is in the options object
      if (!this.options[optionType]) {
        this.options[optionType] = selectedInput.value;
      }
    }
  });
  
}


setProductFromUrl(urlParams) {
  const productID = urlParams.get('product');
  
  // Get all products
  const allProducts = this.getProductData();
  
  // Find the product by handle
  this.currentProduct = allProducts.find((product) => {
      const match = product.handle === productID;
      return match;
  });

  if (this.currentProduct) {
    
    // Reset any existing options
    this.options = {};
    
    // Get the product's options
    if (this.currentProduct.options && this.currentProduct.options.length > 0) {
      const productOptions = this.currentProduct.options[0];
      
      // Set the options from the product
      Object.assign(this.options, productOptions);
      
      // Set inputs directly based on the product options
      for (const [key, value] of Object.entries(productOptions)) {
        const inputElement = this.querySelector(`input[data-option="${key}"][value="${value}"]`);
        
        if (inputElement) {
          inputElement.checked = true;
          this.updateTitles(inputElement);
          if (inputElement.dataset.hasDetails) {
            this.openDetailEvent(inputElement);
          }
        } else {
          // Try to find the input with a different approach
          const allInputs = this.querySelectorAll(`input[data-option="${key}"]`);
          allInputs.forEach(input => {
          });
        }
      }
    } else {
    }
    
    // Update master ID after all options are set
    this.updateState(this.querySelector('input[type="radio"]:checked'));

  } else {
    this.noProductFound();
  }
  
  //remove product and variant parameters
  urlParams.delete('product');
  urlParams.delete('variant');
}

setVariantFromUrl(urlParams) {
  let variant = this.currentProduct.variantId;
  
  if (urlParams.has('variant')){
    variant = urlParams.get('variant');
  }
  
  return variant;
}

setOptionsFromUrl(urlParams) {
  let applied = false;

  const allFieldsets = Array.from(this.querySelectorAll('fieldset.js-option-fieldset'));
  const topFieldsets = allFieldsets.filter((fieldset) => !fieldset.closest('details'));
  const subFieldsets = allFieldsets.filter((fieldset) => fieldset.closest('details'));

  // Pass 1: top-level primary inputs (no parent details).
  topFieldsets.forEach((fieldset) => {
    const optionKey = fieldset.dataset.optionType;
    if (!optionKey || !urlParams.has(optionKey)) return;

    const value = urlParams.get(optionKey);
    const input = fieldset.querySelector(`input[data-option="${optionKey}"][value="${value}"]`);
    if (input) {
      input.checked = true;
      this.updateTitles(input);
      applied = true;
    }
  });

  // Pass 2: suboption inputs, scoped to the fieldset inside the details whose
  // primary input is currently checked. Each primary option (e.g. materiaal) has
  // its own sub-fieldset for the suboption (e.g. kleur), so a plain document-wide
  // querySelector would match the wrong material's suboption input.
  // We set these BEFORE opening the relevant details so openDetails finds a
  // selectedRadio and doesn't recursively trigger updateState with the wrong value.
  subFieldsets.forEach((fieldset) => {
    const optionKey = fieldset.dataset.optionType;
    if (!optionKey || !urlParams.has(optionKey)) return;

    const parentDetails = fieldset.closest('details');
    const primaryInputId = parentDetails.dataset.option;
    if (!primaryInputId) return;

    const primaryInput = this.querySelector(`input[id="${primaryInputId}"]`);
    if (!primaryInput || !primaryInput.checked) return;

    const value = urlParams.get(optionKey);
    const input = fieldset.querySelector(`input[data-option="${optionKey}"][value="${value}"]`);
    if (input) {
      input.checked = true;
      this.updateTitles(input);
      applied = true;
    }
  });

  // Pass 3: open the details for any selected primary input. Suboptions are
  // already set, so openDetails finds a selectedRadio and skips the recursive
  // updateState branch.
  topFieldsets.forEach((fieldset) => {
    const selected = fieldset.querySelector('input[type="radio"]:checked');
    if (selected && selected.dataset.hasDetails) {
      this.openDetailEvent(selected);
    }
  });

  if (applied) {
    this.updateState(this.querySelector('input[type="radio"]:checked'));
  }

  return applied;
}

setInputsFromProduct() {
  if (!this.currentProduct) {
      return;
  }

  
  // Set each input based on our options
  for (const [key, value] of Object.entries(this.options)) {
      const inputElement = this.querySelector(`input[data-option="${key}"][value="${value}"]`);
      
      if (inputElement) {
          inputElement.checked = true;
          this.updateTitles(inputElement);
          if (inputElement.dataset.hasDetails) {
              this.openDetailEvent(inputElement);
          }
      } else {
          // Try to find the input with a different approach
          const allInputs = this.querySelectorAll(`input[data-option="${key}"]`);
          allInputs.forEach(input => {
          });
      }
  }

  // Update options based on the selected inputs
  this.updateState(this.querySelector('input[type="radio"]:checked'));
}


onConfiguratorChange(event) {
  if (this.isUpdating) return;
  
  if (event.target.dataset.type !== 'product-configurator') return;

  this.isUpdating = true;
  
  this.updateState(event.target);

  if (event.target.dataset.hasDetails) {
    this.openDetailEvent(event.target);
  }
}



updateTitles(target) {
  const title = target.value;
  const titleElement = target.closest('fieldset').querySelector('.product-configurator__selected');
  const description = target.dataset.description;
  const descriptionElement = target.closest('fieldset').querySelector('.product-configurator__selected-description');
  // const titleElement = this.querySelector(`legend[data-option="${target.dataset.option}"] .product-configurator__selected`);
  if (titleElement) {
    titleElement.innerHTML = title;
  };
  if (descriptionElement) {
    descriptionElement.innerHTML = description;
  };
  
}

renderProductInfo() {
  fetch(`${Shopify.routes.root}products/${this.currentProduct.handle}?section_id=${this.dataset.section}`)
  .then(response => response.text())

  .then(responseText => {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const destination = document.getElementById(`price-${this.dataset.section}`);
    const source = html.getElementById(
      `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
    );
    if (source && destination) destination.innerHTML = source.innerHTML;

    const galleryDestination = document.getElementById(`MediaWrapperGallery-${this.dataset.section}`);
    const gallerySource = html.getElementById(
      `MediaWrapperGallery-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
    );
    if (gallerySource && galleryDestination) galleryDestination.innerHTML = gallerySource.innerHTML;

    const ModalDestination = document.getElementById(`ProductWrapperModal-${this.dataset.section}`);
    const ModalSource = html.getElementById(
      `ProductWrapperModal-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
    );
    if (ModalSource && ModalDestination) ModalDestination.innerHTML = ModalSource.innerHTML;

    // const destination = this.querySelector('.filter');
    // const source = html.querySelector('.shopify-section');

    // if (source && destination) destination.innerHTML = source.innerHTML;
    this.classList.remove('loading');
    this.isUpdating = false;
  }) .catch(error => {
    this.classList.remove('loading');
    this.isUpdating = false;
});
}

openDetailEvent(target) {
  const id = target.id;
  const details = this.querySelector(`details[data-option="${id}"]`);
  if (details) {
    
    this.closeDetails(details);
    this.openDetails(details);
  } else {
    this.closeDetails(this.details);
  }
  
}

openDetails(details) {
  if (!details) return;
  
  details.open = true;

  // Check if any radio button is already selected
  const selectedRadio = details.querySelector('input[type="radio"]:checked');

  // If no radio button is selected, then select the first one
  if (!selectedRadio) {
      const firstRadio = details.querySelector('input[type="radio"]');
      if (firstRadio) {
          firstRadio.checked = true;
          this.updateState(firstRadio);
      }
  }
}

closeDetails(details) {
  if (!details) return;

  this.details.forEach((detail) => {
    if (detail !== details) {
      detail.open = false
    }
  });

}

  getProductData() {
    this.productData = this.productData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    
    // Check if the product data has the expected structure
    if (this.productData && this.productData.length > 0) {
      const firstProduct = this.productData[0];
      
      if (firstProduct.options && firstProduct.options.length > 0) {
      } else {
      }
    } else {
    }
    
    return this.productData;
  }

fetchProduct() {
  let urlParams = new URLSearchParams(window.location.search);
  if (this.type == 'cart') {
    const url = this.closest('edit-dialog').dataset.url;
    const queryString = url.split('?')[1]; // Get everything after the '?'
    urlParams = new URLSearchParams(queryString);
  }
  const variant = this.setVariantFromUrl(urlParams);
  let section;
  if (this.type == 'cart') {
    section = 'cart-form';
  } else {
    section = 'product-form';
  }
  fetch(`${Shopify.routes.root}products/${this.currentProduct.handle}?variant=${variant}&section_id=${section}`)
    .then(response => response.text())

    .then(responseText => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const destination = this.querySelector('.filter');
      const source = html.querySelector('.shopify-section');

      if (source && destination) destination.innerHTML = source.innerHTML;
      destination.querySelector('.product-form').querySelector('variant-selects').dispatchEvent(new Event('change', { bubbles: true }));
    }).catch(error => {
  });
}

updateMasterId() {
  // Only update the product if options have been selected.
  if (!this.options || Object.keys(this.options).length === 0) {
    this.classList.remove('loading');
    this.isUpdating = false;
    return;
  }
  
  
  // Check if all required options are selected
  const allFieldsets = Array.from(this.querySelectorAll('fieldset.js-option-fieldset'));
  const allOptionTypes = allFieldsets.map(fieldset => fieldset.dataset.optionType);
  
  // If any required option is missing, try to select the first available option
  const missingOptions = allOptionTypes.filter(optionType => !this.options[optionType]);
  if (missingOptions.length > 0) {
    this.ensureAllFieldsetsHaveSelection();
  }
  
  
  // Get all products for debugging
  const allProducts = this.getProductData();
  
  this.currentProduct = allProducts.find((product) => {
    const match = product.options.some(option => {
      const isMatch = this.optionsMatch(this.options, option);
      return isMatch;
    });
    return match;
  });
  
      
  if (this.currentProduct) {
    this.fetchProduct();
    this.renderProductInfo();
  } else {
    this.noProductFound();
    this.classList.remove('loading');
    this.isUpdating = false;
  }
  
}

  noProductFound() {
  }


  
  optionsMatch(selectedOptions, productOptions) {
    
    // Check if all keys in selectedOptions exist in productOptions
    for (const key in selectedOptions) {
      if (!productOptions.hasOwnProperty(key)) {
        return false;
      }
    }
    
    // Check if all values match
    for (const key in selectedOptions) {
      if (selectedOptions[key] !== productOptions[key]) {
        return false;
      }
    }
    
    return true;
  }

updateOptions(target) {
  if (!target) {
    return;
  }
  
  // Get all radio inputs within option fieldsets
  const inputs = Array.from(this.querySelectorAll('fieldset.js-option-fieldset input'));
  
  // Reset options
  this.options = {};

  // Find the fieldset that triggered the event
  const eventTargetFieldset = target.closest('fieldset.js-option-fieldset');
  if (!eventTargetFieldset) {
    return;
  }
  
  const optionType = eventTargetFieldset.dataset.optionType;

  // Collect selected options
  inputs.forEach((input) => {
    if (input.checked) {
      const parentFieldset = input.closest('fieldset.js-option-fieldset');
      if (parentFieldset) {
        const type = parentFieldset.dataset.optionType;
        this.options[type] = input.value;
      }
    }
  });
  this.updateTitles(target);

}
  
  updateAvailableOptions(target) {
    if (!target) return;
  
    const allFieldsets = Array.from(this.querySelectorAll('fieldset.js-option-fieldset'));
    const eventTargetFieldset = target.closest('fieldset.js-option-fieldset');
    let targetFieldsetIndex = allFieldsets.indexOf(eventTargetFieldset);

    // If the change was on a sub-option, we need to start the loop
    // from its parent option fieldset, not the sub-option itself.
    if (eventTargetFieldset.dataset.optionType === 'suboption') {
      const parentDetails = eventTargetFieldset.closest('details');
      if (parentDetails) {
        const parentInputId = parentDetails.dataset.option;
        const parentInput = this.querySelector(`#${parentInputId}`);
        if(parentInput) {
          const parentFieldset = parentInput.closest('fieldset.js-option-fieldset');
          targetFieldsetIndex = allFieldsets.indexOf(parentFieldset);
        }
      }
    }
  
    // From the fieldset after the one that was changed, iterate through the rest.
    for (let i = targetFieldsetIndex + 1; i < allFieldsets.length; i++) {
        const fieldset = allFieldsets[i];
        const optionType = fieldset.dataset.optionType;
        let previouslyCheckedInput = fieldset.querySelector('input:checked');

        // First, update availability for all inputs in this fieldset
        fieldset.querySelectorAll('input').forEach(input => {
            input.disabled = !this.isOptionAvailable(input);
        });

        // If the old selection is now disabled, it needs to be cleared.
        if (previouslyCheckedInput && previouslyCheckedInput.disabled) {
            previouslyCheckedInput.checked = false;
            previouslyCheckedInput = null; // The old selection is no longer valid.
        }

        // If there's no valid selection exists, select the first available option.
        if (!previouslyCheckedInput) {
            const firstAvailable = fieldset.querySelector('input:not([disabled])');
            if (firstAvailable) {
                firstAvailable.checked = true;
            }
        }
        
        // Finally, update our central `options` object and UI titles based on the
        // final state of this fieldset, before moving to the next.
        const finalSelection = fieldset.querySelector('input:checked');
        if (finalSelection) {
            this.options[optionType] = finalSelection.value;
            this.updateTitles(finalSelection);
        } else {
            // If no option is available/selected, remove it from our options
            // and clear the title. ONLY if the fieldset is visible.
            const parentDetails = fieldset.closest('details');
            if (!parentDetails || parentDetails.open) {
                delete this.options[optionType];
            }
            const titleElement = fieldset.querySelector('.product-configurator__selected');
            if (titleElement) titleElement.innerHTML = '';
            const descriptionElement = fieldset.querySelector('.product-configurator__selected-description');
            if (descriptionElement) descriptionElement.innerHTML = '';
        }
    }
  }

  isOptionAvailable(input) {
    const parentFieldset = input.closest('fieldset.js-option-fieldset');
    const optionType = parentFieldset.dataset.optionType;
    const optionValue = input.value;
    
    const relevantOptions = {};
    const allFieldsets = Array.from(this.querySelectorAll('fieldset.js-option-fieldset'));
    const parentFieldIndex = allFieldsets.indexOf(parentFieldset);

    for (let i = 0; i < parentFieldIndex; i++) {
        const prevFieldset = allFieldsets[i];
        const prevOptionType = prevFieldset.dataset.optionType;
        if (this.options[prevOptionType]) {
            relevantOptions[prevOptionType] = this.options[prevOptionType];
        }
    }
    
    const testOptions = { ...relevantOptions, [optionType]: optionValue };

    // See if any product exists that is compatible with these partial options
    let productExists = this.getProductData().some((product) => {
        return this.isPartialMatch(testOptions, product.options[0]);
    });

    return productExists;
}

isPartialMatch(partialOptions, fullOptions) {
  for (const key in partialOptions) {
      if (partialOptions[key] !== fullOptions[key]) {
          return false;
      }
  }
  return true;
}


}
customElements.define('product-configurator', ProductConfigurator);


class EditForm extends HTMLElement {
constructor() {
  super();
  this.parentDialog = this.closest('edit-dialog');
  this.key = this.parentDialog.dataset.key;
  this.quantity = this.parentDialog.dataset.quantity;
  this.form = this.querySelector('form');
  this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
  this.cart = document.querySelector('cart-drawer');
  this.addEventListener('change', this.onFormChange);


}

onFormChange(event) {
  this.updateProperties(event);
}

updateProperties(event) {
  if (event.target.dataset.option === 'properties') {
    
    event.target.closest('fieldset').querySelector('.product-configurator__selected').innerHTML = event.target.value;
  }
}


async onSubmitHandler(event) {
  event.preventDefault();

  const submitButton = this.querySelector('[type="submit"]');
  if (submitButton.classList.contains('loading')) return; 
  submitButton.setAttribute('aria-disabled', true);
    submitButton.classList.add('loading');
    this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

  const existingVariantId = this.key; // The existing item variant ID

  try {
    // Step 1: Remove the existing item
    const removeResponse = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: existingVariantId,
        quantity: 0, // Set to 0 to remove
      }),
    });

    if (!removeResponse.ok) {
      throw new Error(`Failed to remove item: ${await removeResponse.text()}`);
    }

    const updatedCartAfterRemoval = await removeResponse.json();

    // Step 2: Add the new item
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    const formData = new FormData(this.form);
    if (this.cart) {
      console.log(this.cart);
;        formData.append(
        'sections',
        this.cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      this.cart.setActiveElement(document.activeElement);
    }
    config.body = formData;
    


    const addResponse = await fetch(`${routes.cart_add_url}`, config);

    if (!addResponse.ok) {
      throw new Error(`Failed to add item: ${await addResponse.text()}`);
    }

    const updatedCartAfterAddition = await addResponse.json();

    // Update the cart UI
    this.updateCartUI(updatedCartAfterAddition);
    submitButton.classList.remove('loading');
    submitButton.removeAttribute('aria-disabled');
    this.querySelector('.loading-overlay__spinner').classList.add('hidden');

  } catch (error) {
    this.showError();
  }
}

updateCartUI(response) {
  this.cart.renderContents(response);

}

showError() {
  this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
  document.getElementById('notification-cart-errors').textContent = window.cartStrings.error;
  this.disableLoading();
}



disableLoading() {
  this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
}
}

customElements.define('edit-form', EditForm);
